/** One bank account in the list: its notation, label, currency, source, state and the actions it offers. */
import { Badge, Button, Dropdown, Icon } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import type { PartyBankAccount } from '../hooks/use-party-bank-accounts';
import type { BankAccountActions } from '../utils/bank-account-actions';
import { formatBankAccountNumber, secondaryIban } from '../utils/format-bank-account';
import { BankAccountStateBadge } from './BankAccountStateBadge';

/** What the row asks its list to open. */
export type BankAccountRowAction = 'sign' | 'edit' | 'history' | 'reject' | 'revoke' | 'deactivate';

interface PartyBankAccountRowProps {
  account: PartyBankAccount;
  actions: BankAccountActions;
  onAction: (action: BankAccountRowAction, account: PartyBankAccount) => void;
}

export function PartyBankAccountRow({ account, actions, onAction }: PartyBankAccountRowProps) {
  const t = useFeatureTranslation('partyBankAccounts');
  const tSource = useEnumTranslation('bankAccountSource');
  const iban = secondaryIban(account);
  const facts = [account.currencyCode ?? t('row.anyCurrency'), tSource(account.source)];
  const muted = !account.isActive;

  return (
    <li
      className='flex flex-wrap items-center gap-x-md gap-y-sm rounded-xl border border-border-base bg-background-input px-md py-sm'
      data-testid={`bank-account-row-${account.id}`}
      data-state={account.verificationState}
    >
      <div className='min-w-0 flex-1 basis-56'>
        <p
          className={`font-mono tabular-nums text-sm font-medium break-all ${
            muted ? 'text-foreground-muted' : 'text-foreground-base'
          }`}
        >
          {formatBankAccountNumber(account)}
        </p>
        {account.label && <p className='text-sm text-foreground-base'>{account.label}</p>}
        {iban && (
          <p className='font-mono tabular-nums text-xs text-foreground-muted break-all'>
            {t('row.iban', { iban })}
          </p>
        )}
        {account.bic && (
          <p className='font-mono tabular-nums text-xs text-foreground-muted'>
            {t('row.bic', { bic: account.bic })}
          </p>
        )}
        <p className='text-xs text-foreground-muted'>{facts.join(' · ')}</p>
      </div>

      <div className='flex flex-wrap items-center gap-xs'>
        <BankAccountStateBadge state={account.verificationState} />
        {account.isDefault && (
          <Badge size='sm' color='primary'>
            {t('row.default')}
          </Badge>
        )}
        {!account.isActive && (
          <Badge size='sm' color='neutral'>
            {t('row.inactive')}
          </Badge>
        )}
      </div>

      <div className='flex items-center gap-xs'>
        {actions.sign && (
          <Button
            variant='default'
            outline='default'
            size='sm'
            icon='file-signature'
            testId={`bank-account-sign-${account.id}`}
            onClick={() => onAction('sign', account)}
          >
            {t('actions.sign')}
          </Button>
        )}
        <Dropdown
          triggerButton={
            <Button variant='ghost' size='sm' aria-label={t('row.actions')}>
              <Icon name='ellipsis-vertical' size='sm' />
            </Button>
          }
          align='bottom end'
          size='sm'
        >
          {actions.edit && (
            <Dropdown.Item icon='pen' onClick={() => onAction('edit', account)}>
              {t('actions.edit')}
            </Dropdown.Item>
          )}
          <Dropdown.Item icon='clock-rotate-left' onClick={() => onAction('history', account)}>
            {t('actions.history')}
          </Dropdown.Item>
          {actions.reject && (
            <Dropdown.Item
              icon='ban'
              className='text-error-text'
              onClick={() => onAction('reject', account)}
            >
              {t('actions.reject')}
            </Dropdown.Item>
          )}
          {actions.revoke && (
            <Dropdown.Item
              icon='ban'
              className='text-error-text'
              onClick={() => onAction('revoke', account)}
            >
              {t('actions.revoke')}
            </Dropdown.Item>
          )}
          {actions.deactivate && (
            <Dropdown.Item icon='circle-xmark' onClick={() => onAction('deactivate', account)}>
              {t('actions.deactivate')}
            </Dropdown.Item>
          )}
        </Dropdown>
      </div>
    </li>
  );
}
